/**
 * Button-action parsing — a verbatim mirror of the canonical validator in
 * `packages/db-types/src/panel-definition.ts` (`panelButtonActionSchema`).
 *
 * The catalog validator rejects a malformed action at WRITE time. This parser is
 * the second half of that defence: a renderer may be handed a definition that
 * was written before a rule tightened, or by a caller that never went through a
 * server-side write at all (a plugin building a panel in its own code). So the
 * grammar is re-checked here, immediately before the action can do anything.
 *
 * Anything that does not parse returns `null`, and the renderer treats the
 * button as inert. It NEVER falls back to "probably a URL".
 */
import type { PanelAction } from './types';

/** Mirrors `PANEL_EMIT_TOKEN_MAX_LENGTH`. */
export const PANEL_EMIT_TOKEN_MAX_LENGTH = 64;
/** Mirrors `PANEL_ACTION_MAX_LENGTH`. */
export const PANEL_ACTION_MAX_LENGTH = 2048;

const EMIT_TOKEN_PATTERN = new RegExp(`^[a-z0-9][a-z0-9_-]{0,${PANEL_EMIT_TOKEN_MAX_LENGTH - 1}}$`);
const ARCHE_ID_PATTERN = /^arc_[a-f0-9]{32}$/;

/**
 * Parse a panel button action string into the intent it denotes, or `null` if
 * it is not a well-formed action.
 *
 * The `open_url` check parses the URL for real rather than pattern-matching it:
 * scheme must be exactly `https:` and no whitespace may appear anywhere, which
 * is what rejects `javascript:`, `data:`, `vbscript:`, protocol-relative
 * (`//host`) and whitespace-obfuscation tricks.
 */
export function parsePanelAction(action: string): PanelAction | null {
  if (typeof action !== 'string' || action.length === 0) return null;
  if (action.length > PANEL_ACTION_MAX_LENGTH) return null;

  if (action === 'close') return { type: 'close' };

  if (action.startsWith('emit:')) {
    const token = action.slice('emit:'.length);
    return EMIT_TOKEN_PATTERN.test(token) ? { type: 'emit', token } : null;
  }

  if (action.startsWith('open_arche:')) {
    const archeId = action.slice('open_arche:'.length);
    return ARCHE_ID_PATTERN.test(archeId) ? { type: 'open_arche', archeId } : null;
  }

  if (action.startsWith('open_url:')) {
    if (/\s/.test(action)) return null;
    const url = action.slice('open_url:'.length);
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return null;
    }
    return parsed.protocol === 'https:' ? { type: 'open_url', url } : null;
  }

  return null;
}
